import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Checkbox, InputLabel, MenuItem, Select as MuiSelect } from '@mui/material';
import { HasAccessTo } from 'app/components/Authorization';
import { SearchInput } from 'app/components';
import { EVENT } from 'app/common/Authorization/entities';
import { EDIT } from 'app/common/Authorization/permissions';
import { SyncStatus } from 'app/common/SyncStatus';
import { TranslationKeys } from 'app/translations';

import { DivRowStyled, DropdownStyled, FormControlStyled, SpacerStyled } from './EventsToolbar.styles';

type EventsToolbarProps = {
  processEvents: () => void;
  processAllEvents: () => void;
  hasSelectedItems: boolean;
  selectedStatuses: SyncStatus[];
  onChangeStatus: (filter: { name: string; value: SyncStatus[] }) => void;
  onSearch: (searchQuery: string) => void;
  intl: any;
};

const ALL_SYNC_STATUSES = Object.values(SyncStatus);

const EventsToolbar = (props: EventsToolbarProps) => {
  const { processEvents, processAllEvents, hasSelectedItems, selectedStatuses, onChangeStatus, onSearch, intl } = props;

  const disabledIfNoAccessToEventEdit = !HasAccessTo(EVENT, EDIT);

  const dropdownOptions = {
    process: [
      {
        title: intl.formatMessage({ id: 'events.processAction' }),
        tooltip: intl.formatMessage({ id: 'tooltip_events_nothing_selected' }),
        confirmDialog: {
          title: intl.formatMessage({ id: 'events.processConfirmTitle' }),
          message: intl.formatMessage({ id: 'events.processConfirmMessage' }),
        },
        action: processEvents,
        disabled: disabledIfNoAccessToEventEdit || !hasSelectedItems,
      },
      {
        title: intl.formatMessage({ id: 'events.processAllAction' }),
        tooltip: intl.formatMessage({ id: 'tooltip_events_nothing_selected' }),
        confirmDialog: {
          title: intl.formatMessage({ id: 'events.processConfirmTitle' }),
          message: intl.formatMessage({ id: 'events.processAllConfirmMessage' }),
        },
        action: processAllEvents,
        disabled: disabledIfNoAccessToEventEdit,
      },
    ],
  };

  const handleStatusChange = React.useCallback(
    (event: any) => {
      onChangeStatus({ name: 'status', value: event.target.value as SyncStatus[] });
    },
    [onChangeStatus],
  );

  const handleSearch = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSearch(event.target.value);
    },
    [onSearch],
  );

  const statusLabel = intl.formatMessage({ id: TranslationKeys.global_status });

  return (
    <DivRowStyled>
      <SearchInput onChange={handleSearch} style={{ marginRight: '8px' }} />
      <FormControlStyled>
        <InputLabel>{statusLabel}</InputLabel>
        <MuiSelect
          multiple
          value={selectedStatuses}
          onChange={handleStatusChange}
          renderValue={(selected: unknown) => (
            <div>
              {(selected as SyncStatus[]).map((status, index) => (
                <span key={status}>
                  {index > 0 && ', '}
                  <FormattedMessage id={status} />
                </span>
              ))}
            </div>
          )}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: '400px',
              },
            },
          }}
        >
          {ALL_SYNC_STATUSES.map(status => (
            <MenuItem key={status} value={status}>
              <Checkbox checked={selectedStatuses.includes(status)} />
              <FormattedMessage id={status} />
            </MenuItem>
          ))}
        </MuiSelect>
      </FormControlStyled>
      <SpacerStyled />
      <DropdownStyled options={dropdownOptions.process} intl />
    </DivRowStyled>
  );
};

export default injectIntl(EventsToolbar);
